export interface SendAsAccount {
  name?: string
  email: string
  [key: string]: unknown
}

export interface WorkHour {
  enabled: boolean
  start: number
  end: number
}

export interface EditorFont {
  editorFontFamily: string
  editorFontSizeString: string
  editorFontColorReFwd: string
  editorFontColorNew: string
}

export interface ICSSubscriptionSync {
  id: string
  url: string
  displayName: string
  color: string
  refreshInterval: string
  isSync: boolean
}

export interface CalendarSettings {
  // sync 대상 필드
  defaultDuration: number
  defaultReminderTime: number
  defaultReminderTimeForAll: number
  firstdayOfWeek: number
  hideDeclinedMeeting: boolean
  hideCanceledMeeting: boolean
  showWeekNumber: boolean
  firstWeekNumber: number
  /** 동기화 대상 캘린더 경로 목록 (host 제외 path). null=미초기화, []=모두 비활성. @see libs/dav/davPath */
  calendarSyncTargets: string[] | null
  icsSubscriptions: ICSSubscriptionSync[] | null
  showSecondaryTimezone: boolean
  secondaryTimezone: string | null
  secondaryTimezoneLabel: string | null
  /** 기본 캘린더 경로 (host 제외 path). @see libs/dav/davPath */
  defaultCalendar: string | null
  workHours: WorkHour[]
}

export interface LocalCalendarSettings {
  calendarViewType: string
  taskDrawerOpen: boolean
  lastAddedCalendar: string | null
  primaryTimezone: string | null
  primaryTimezoneLabel: string | null
  autoAcceptSharedCalendarNoti: boolean
}

export interface TodoSortBy {
  [key: string]: string
}

export interface TodoSettings {
  // sync 대상 필드
  /** 동기화 대상 할일 폴더 경로 목록 (host 제외 path). null=미초기화, []=모두 비활성. @see libs/dav/davPath */
  todoSyncTargets: string[] | null
  defaultReminderStartTime: number
  defaultReminderTimeForToday: number
  defaultReminderTimeForFuture: string
  defaultDueDate: string
  /** 기본 할일 폴더 경로 (host 제외 path). @see libs/dav/davPath */
  defaultFolder: string | null
}

export interface LocalTodoSettings {
  filterType: string
  sortBy: TodoSortBy
  sortDirection: Record<string, string>
  collapsedSectionKeys: Record<string, boolean>
  lastAddedFolder: string | null
}

export interface AiSettings {
  summary: boolean
  language: string
}

export interface AlwaysEmailMap {
  [key: string]: string
}

export interface AccountSettings {
  accountName: string | null
  accountColor: string | null
  accountSenderName: string | null
  alwaysCc: AlwaysEmailMap
  alwaysBcc: AlwaysEmailMap
  replyTo: string
  signatureForNew: string
  signatureForReplyForward: string
}

export interface GeneralSettings {
  // locale 은 sync 대상이 아니다. local-only 로 분리되어 localSettings.general.locale 로 이동했다.
  // (서버/다른 기기와 동기화하지 않고 이 기기에서만 유지) @see LocalGeneralSettings
  dateFormat: string
  timeFormat: string
}

// general 카테고리 중 local-only 필드. locale 은 서버로 전송하지 않고 서버값도 무시한다.
export interface LocalGeneralSettings {
  locale: string
}

export interface EmailSettings {
  mailUseThread: boolean
  visibleThreadItems: string[]
  viewMode: string
  selectionBehavior: string
  editorFont: EditorFont
  recipientsSearchTarget: number[]
  recipientsSearchResultOrder: number[]
  confirmBeforeSend: boolean
  alwaysRequestReadReceipt: boolean
  showOriginalMessage: boolean
  autoAdvanced: number
  isUnknownAlwaysShowExternalResources: number
  isKnownAlwaysShowExternalResources: number
  isMarkAsReadWhenOpen: boolean
  isMarkAsReadWhenDeleted: boolean
  isAutoMarkAsReadWhenReplyingOrForwarding: boolean
  /**
   * 즐겨찾기 메일함 항목 목록(배열 순서 = 표시 순서). 웹·데스크탑 공용 canonical 경로 또는 'virtual:<id>'.
   * undefined = 미커스터마이즈(role 기반 기본값을 계산해 표시, 서버 미저장), [] = 사용자가 전부 제거.
   * initialState 에 기본값을 두지 않는다(기본값이 서버에 저장되지 않도록). @see ./favoriteMailboxes
   */
  favoriteMailboxes?: string[]
}

export interface LocalEmailSettings {
  theme: string
  sendAsAccounts: SendAsAccount[]
  groupMails: SendAsAccount[]
  defaultSendAsAccount: string | SendAsAccount
  emailListPageSize: number
  useEmailSync: boolean
  syncFrequency: number
}

export interface LocalContactsSettings {
  contactOrganizationExpandedKeys: string[] | null
}

export interface LocalSettings {
  email: LocalEmailSettings
  contacts: LocalContactsSettings
  calendar: LocalCalendarSettings
  todo: LocalTodoSettings
  // general.locale 을 local-only 로 보관. sync 카테고리(general)에서 분리됨. @see LocalGeneralSettings
  general: LocalGeneralSettings
}

// ─── Settings version ──────────────────────────────────────
// IndexedDB 마이그레이션 + 서버 스키마 버전 공용
// v4: CalDAV/CardDAV 동기화 폴더 식별자(calendarSyncTargets/defaultCalendar,
//     todoSyncTargets/defaultFolder)를 host 포함 절대 URL → host 제외 path 로 정규화(sagas.ts settingsMigration 참고)
export const settingsVersion = 4

// ─── Migration accessor ────────────────────────────────────
// settingsMigration이 DB와 plain 객체 모두에서 동작하도록 추상화
export interface MigrationAccessor<T> {
  type: string
  getValue: (target: T, key: string) => unknown | Promise<unknown>
  setValue: (target: T, key: string, value: unknown) => unknown | Promise<unknown>
  removeValue: (target: T, key: string) => unknown | Promise<unknown>
}

export interface SettingsState {
  // 버전 (DB 마이그레이션 + 서버 데이터 버전 비교 공용)
  version: number

  // sync 카테고리 (Server path 구조와 동일)
  account: AccountSettings
  general: GeneralSettings
  email: EmailSettings
  calendar: CalendarSettings
  todo: TodoSettings
  ai: AiSettings

  // local-only
  localSettings: LocalSettings

  // ETag (서버 sync용, 서버 전송 시 제외)
  eTag: string | null

  [key: string]: unknown
}

// ─── Valid settings categories ─────────────────────────────
// SettingsState에서 명시적으로 선언된 카테고리 키 목록.
// CHANGE_SETTING saga는 dot-path의 첫 세그먼트(category)가 이 목록에 포함된 경우에만 처리한다.
// 인덱스 시그니처([key: string]: unknown)로 인해 임의의 키가 허용되는 것을 방지한다.
export const VALID_SETTINGS_CATEGORIES = [
  'version',
  'eTag',
  'account',
  'general',
  'email',
  'calendar',
  'todo',
  'ai',
  'localSettings',
] as const

export type SettingsCategory = (typeof VALID_SETTINGS_CATEGORIES)[number]

export const isValidSettingsCategory = (key: string): key is SettingsCategory =>
  (VALID_SETTINGS_CATEGORIES as readonly string[]).includes(key)

export type ChangeSettingPayload = Record<string, unknown>

export interface SetThemePayload {
  theme: string
}

export interface SetLocalePayload {
  locale: string
}

// OMD-225 Phase 1: RELOAD를 2국면으로 나누기 위한 타입.
export interface DavMigratedCategories {
  calendar: boolean
  todo: boolean
}

export interface ReloadSettingsPhase1Result {
  davMigratedCategories: DavMigratedCategories
  // v2→v3 서명 키 마이그레이션(localSettings.account → account)이 실제 값을 옮겼는지 여부.
  // true면 loaded 이후 account를 1회 업로드해 서버에 반영한다.
  signatureMigrated: boolean
  name?: string
  email?: string
}

export interface ReloadSettingsPayload {
  name?: string
  email?: string
  // Phase-1(DB 로드+SET_STATE+SETUP) 완료 시 호출된다. landing은 이 시점에 sync()를 호출한다.
  onComplete?: (result: ReloadSettingsPhase1Result) => void
}

export interface ReloadSettingsFinalizePayload {
  davMigratedCategories: DavMigratedCategories
  signatureMigrated: boolean
  name?: string
  email?: string
}

export interface MigrateFavoriteMailboxesPayload {
  // 구버전 로컬 즐겨찾기 키(DataManager.infoDBKey.favoriteMailboxList, 운영은 이메일 해시 기반).
  localKey: string
  // 메일함 로드 후 DataManager 가 계산한 role 기반 기본 즐겨찾기 항목 목록.
  defaultEntries: string[]
  // 구버전 로컬 id 목록 → 즐겨찾기 항목 변환기(saga 는 메일함에 접근할 수 없으므로 DataManager 가 주입).
  // 현재 메일함으로 해석되지 않는 id(삭제된 폴더)는 버린다.
  toEntries: (legacyIds: string[]) => string[]
  // 판정을 끝냈으면(서버 로드 loaded) true, 아직 판정할 수 없으면(loaded 아님/오류) false.
  // false 면 DataManager 가 다음 메일함 변경 시 재시도한다.
  onComplete?: (completed: boolean) => void
}

// ─── Local key routing ─────────────────────────────────────
// CHANGE_SETTING saga와 STORED_SETTINGS에서 공유

export const LOCAL_KEY_ROUTING: Record<string, keyof LocalSettings> = {
  // email 그룹 (flat keys)
  theme: 'email',
  sendAsAccounts: 'email',
  groupMails: 'email',
  defaultSendAsAccount: 'email',
  emailListPageSize: 'email',
  useEmailSync: 'email',
  syncFrequency: 'email',
  // contacts 그룹
  contactOrganizationExpandedKeys: 'contacts',
  // general 그룹 — locale 은 local-only (sync 안 함). dateFormat/timeFormat 은 sync 대상이므로 SYNC_KEY_ROUTING 에 둔다.
  locale: 'general',
}

// ─── Sync key routing ──────────────────────────────────────
// CHANGE_SETTING saga에서 flat key를 카테고리 그룹으로 라우팅

export const SYNC_KEY_ROUTING: Record<string, keyof SettingsState> = {
  // account
  accountName: 'account',
  accountColor: 'account',
  accountSenderName: 'account',
  alwaysCc: 'account',
  alwaysBcc: 'account',
  replyTo: 'account',
  signatureForNew: 'account',
  signatureForReplyForward: 'account',
  // general — locale 은 sync 안 함(local-only, LOCAL_KEY_ROUTING 참조). dateFormat/timeFormat 만 sync.
  dateFormat: 'general',
  timeFormat: 'general',
  // email
  mailUseThread: 'email',
  visibleThreadItems: 'email',
  viewMode: 'email',
  selectionBehavior: 'email',
  editorFont: 'email',
  recipientsSearchTarget: 'email',
  recipientsSearchResultOrder: 'email',
  confirmBeforeSend: 'email',
  alwaysRequestReadReceipt: 'email',
  showOriginalMessage: 'email',
  autoAdvanced: 'email',
  isUnknownAlwaysShowExternalResources: 'email',
  isKnownAlwaysShowExternalResources: 'email',
  isMarkAsReadWhenOpen: 'email',
  isMarkAsReadWhenDeleted: 'email',
  isAutoMarkAsReadWhenReplyingOrForwarding: 'email',
}

// ─── initialState ──────────────────────────────────────────
// reducers.ts에서 분리: 순환 참조 방지를 위해 types.ts에 위치
import { CONTACT_TYPE, MAILBOX_TYPE, TODO_FILTER_TYPE } from 'popups/Settings/def'
import { SortType } from 'pages/todo/modules/TodoFilter'

export const initialState: SettingsState = {
  // version은 초기값은 0으로 하고, 로딩되거나, 저장을 하는 경우에 현재 버전으로 저장되어야 함.
  // 기본값으로 현재 버전을 사용하면 저장된 데이터에 버전 정보가 없는 경우, 최신 버전으로 간주하게 되어 마이그레이션이 제대로 동작하지 않을 수 있음.
  version: 0,
  eTag: null,

  // === account (sync) ===
  account: {
    accountName: null,
    accountColor: null,
    accountSenderName: null,
    alwaysCc: {},
    alwaysBcc: {},
    replyTo: '',
    signatureForNew: 'default',
    signatureForReplyForward: 'none',
  },

  // === general (sync) ===
  // locale 은 여기 두지 않는다 — local-only 로 localSettings.general.locale 로 이동.
  general: {
    dateFormat: 'YYYY-MM-DD',
    timeFormat: 'HH:mm',
  },

  // === email (sync) ===
  email: {
    mailUseThread: true,
    visibleThreadItems: [MAILBOX_TYPE.SENT],
    viewMode: 'splitHorizontal', // 'noSplit', 'splitHorizontalReverse'
    selectionBehavior: 'firstMessage', // 'manually'
    editorFont: {
      editorFontFamily: 'Malgun Gothic, Calibri, Arial, sans-serif',
      editorFontSizeString: '11pt',
      editorFontColorReFwd: '#1f497d',
      editorFontColorNew: '#000000',
    },
    recipientsSearchTarget: [
      CONTACT_TYPE.COMPANY,
      CONTACT_TYPE.ORGANIZATION,
      CONTACT_TYPE.CUSTOMER,
      CONTACT_TYPE.PERSONAL,
      CONTACT_TYPE.SUGGESTED,
    ],
    recipientsSearchResultOrder: [
      CONTACT_TYPE.COMPANY,
      CONTACT_TYPE.ORGANIZATION,
      CONTACT_TYPE.CUSTOMER,
      CONTACT_TYPE.PERSONAL,
      CONTACT_TYPE.SUGGESTED,
    ],
    confirmBeforeSend: true,
    alwaysRequestReadReceipt: false,
    showOriginalMessage: false,
    autoAdvanced: 1, // 0 - prev, 1 - next, 2 - return to list
    isUnknownAlwaysShowExternalResources: 0, // 0 - always show, 1 - ask
    isKnownAlwaysShowExternalResources: 0, // 0 - always show, 1 - ask
    isMarkAsReadWhenOpen: true,
    isMarkAsReadWhenDeleted: false,
    isAutoMarkAsReadWhenReplyingOrForwarding: false,
  },

  // === calendar (sync) ===
  calendar: {
    defaultDuration: 30,
    defaultReminderTime: -900,
    defaultReminderTimeForAll: -25200,
    firstdayOfWeek: 7,
    hideDeclinedMeeting: true,
    hideCanceledMeeting: true,
    showWeekNumber: false,
    firstWeekNumber: 0,
    calendarSyncTargets: null,
    icsSubscriptions: null,
    showSecondaryTimezone: false,
    secondaryTimezone: null,
    secondaryTimezoneLabel: null,
    defaultCalendar: null,
    workHours: [
      { /*일*/ enabled: false, start: 540, end: 1080 },
      { /*월*/ enabled: true, start: 540, end: 1080 },
      { /*화*/ enabled: true, start: 540, end: 1080 },
      { /*수*/ enabled: true, start: 540, end: 1080 },
      { /*목*/ enabled: true, start: 540, end: 1080 },
      { /*금*/ enabled: true, start: 540, end: 1080 },
      { /*토*/ enabled: false, start: 540, end: 1080 },
    ],
  },

  // === todo (sync) ===
  todo: {
    todoSyncTargets: null,
    defaultReminderStartTime: 540, // 9AM
    defaultReminderTimeForToday: 0, // Auto, 10, 30, 60, 120, 180
    defaultReminderTimeForFuture: 'dueDate', // dueDate, dayBefore, weekBefore
    defaultDueDate: 'no-date', // today, tomorrow, no-date
    defaultFolder: null,
  },

  // === ai (sync) ===
  ai: {
    summary: false,
    language: 'Korean',
  },

  // === localSettings (local-only) ===
  localSettings: {
    email: {
      theme: 'default', // default, dark
      sendAsAccounts: [],
      groupMails: [],
      defaultSendAsAccount: '',
      emailListPageSize: 15,
      useEmailSync: true,
      syncFrequency: -1,
    },
    contacts: {
      contactOrganizationExpandedKeys: null,
    },
    calendar: {
      calendarViewType: 'week', // month, week, day
      taskDrawerOpen: true,
      lastAddedCalendar: null,
      primaryTimezone: null,
      primaryTimezoneLabel: null,
      autoAcceptSharedCalendarNoti: true,
    },
    todo: {
      filterType: TODO_FILTER_TYPE.all, // today, important, planned, overdue, completed
      sortBy: {
        [TODO_FILTER_TYPE.all]: SortType.Title,
        [TODO_FILTER_TYPE.today]: SortType.Created,
        [TODO_FILTER_TYPE.important]: SortType.DueDate,
        [TODO_FILTER_TYPE.planned]: SortType.Planned,
        [TODO_FILTER_TYPE.overdue]: SortType.Overdue,
        [TODO_FILTER_TYPE.completed]: SortType.Completed,
        [TODO_FILTER_TYPE.focusDate]: SortType.Created,
      },
      sortDirection: {},
      collapsedSectionKeys: {},
      lastAddedFolder: null,
    },
    // locale 은 local-only (sync 안 함). 서버/다른 기기와 동기화하지 않고 이 기기에서만 유지.
    general: {
      locale: 'ko-KR',
    },
  },
}
